<template>
    <div class="w-full">
        <div class="w-full text-base border-b p-1 mb-2 tracking-widest flex justify-between items-center">
            <span>Assignment History</span>
            <div class="flex space-x-1">
                <span class="text-xs px-2 py-0.5 rounded" :class="lifecycleBadgeClass">
                    {{ lifecycleLabel }}
                </span>
                <span v-if="warrantyStatus" class="text-xs px-2 py-0.5 rounded" :class="warrantyBadgeClass">
                    Warranty: {{ warrantyStatusLabel }}
                </span>
            </div>
        </div>

        <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            <strong>Never paste credentials here.</strong> No BIOS / BitLocker / recovery keys / passwords — those live in the password manager. Free-text fields are scanned and obvious credentials are rejected.
        </div>

        <div class="flex space-x-2 mb-3">
            <button
                type="button"
                @click="openIssueDialog"
                :disabled="!canIssue || busy"
                :class="actionButtonClass(canIssue)"
            >
                Issue Item
            </button>
            <button
                type="button"
                @click="openReturnDialog"
                :disabled="!hasOpenAssignment || busy"
                :class="actionButtonClass(hasOpenAssignment)"
            >
                Record Return
            </button>
            <button
                v-if="canRetire"
                type="button"
                @click="openRetireDialog"
                :disabled="busy"
                class="rounded px-3 py-1 text-sm border border-red-500 text-red-700 hover:bg-red-50"
            >
                Retire
            </button>
            <button
                v-if="isRetired"
                type="button"
                @click="unretire"
                :disabled="busy"
                class="rounded px-3 py-1 text-sm border border-blue-500 text-blue-700 hover:bg-blue-50"
            >
                Un-retire
            </button>
            <button
                v-if="isReturned"
                type="button"
                @click="returnToStock"
                :disabled="busy"
                class="rounded px-3 py-1 text-sm border border-green-600 text-green-700 hover:bg-green-50"
            >
                Mark Available
            </button>
        </div>

        <div v-if="!assignments.length" class="text-sm text-gray-500 italic p-2">
            No assignment history yet.
        </div>

        <ul v-else class="space-y-2">
            <li
                v-for="a in assignments"
                :key="a.id"
                class="border rounded p-3"
                :class="a.is_open ? 'border-accent-primary bg-accent-primary/5' : 'border-gray-200'"
            >
                <!-- Display mode -->
                <div v-if="editingAssignmentId !== a.id" class="flex justify-between items-start text-sm">
                    <div class="flex-1">
                        <div class="font-semibold">
                            {{ a.user ? `${a.user.first_name} ${a.user.last_name}` : 'Unknown' }}
                            <span v-if="a.is_open" class="ml-2 text-xs text-accent-primary">Currently held</span>
                        </div>
                        <div class="text-xs text-gray-600">
                            Issued {{ formatDate(a.issued_on) }}
                            <span v-if="a.issued_by_user">
                                by {{ a.issued_by_user.first_name }} {{ a.issued_by_user.last_name }}
                            </span>
                            <span v-if="a.condition_at_issue"> · condition: {{ a.condition_at_issue }}</span>
                        </div>
                        <div v-if="a.returned_on" class="text-xs text-gray-600">
                            Returned {{ formatDate(a.returned_on) }}
                            <span v-if="a.returned_by_user">
                                by {{ a.returned_by_user.first_name }} {{ a.returned_by_user.last_name }}
                            </span>
                            <span v-if="a.condition_at_return"> · condition: {{ a.condition_at_return }}</span>
                        </div>
                        <div v-if="a.notes" class="text-xs text-gray-700 mt-1 whitespace-pre-line">
                            {{ a.notes }}
                        </div>
                    </div>
                    <button
                        type="button"
                        class="text-xs text-accent-primary hover:underline ml-2"
                        @click="startEditAssignment(a)"
                        title="Edit this assignment record"
                    >Edit</button>
                </div>

                <!-- Edit mode — same row swaps to a small form -->
                <div v-else class="text-sm space-y-2">
                    <div class="font-semibold">
                        Editing assignment to {{ a.user ? `${a.user.first_name} ${a.user.last_name}` : 'Unknown' }}
                    </div>
                    <div class="grid grid-cols-2 gap-2 text-xs">
                        <div class="flex flex-col">
                            <label class="text-gray-500">Issued on</label>
                            <input type="date" v-model="editForm.issued_on" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Returned on</label>
                            <input type="date" v-model="editForm.returned_on" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Condition at issue</label>
                            <input type="text" v-model="editForm.condition_at_issue" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Condition at return</label>
                            <input type="text" v-model="editForm.condition_at_return" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col col-span-2">
                            <label class="text-gray-500">Notes</label>
                            <textarea v-model="editForm.notes" class="border rounded p-1" rows="3" />
                        </div>
                    </div>
                    <div class="flex justify-end space-x-2 pt-1">
                        <button type="button" @click="cancelEditAssignment" class="text-xs px-2 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="busy" @click="saveEditAssignment(a)" class="text-xs px-2 py-1 rounded bg-dark-primary text-white">Save</button>
                    </div>
                </div>

                <div v-if="editingAssignmentId !== a.id" class="mt-2 grid grid-cols-2 gap-3 text-xs">
                    <div>
                        <div class="font-medium text-gray-500 mb-1">Issue (before) photos</div>
                        <PhotoStrip :photos="issuePhotosFor(a)" />
                        <button
                            v-if="a.is_open || a.returned_on === null"
                            type="button"
                            class="text-xs text-accent-primary hover:underline"
                            @click="openPhotoUpload(a, 'issue')"
                        >+ add issue photo</button>
                    </div>
                    <div>
                        <div class="font-medium text-gray-500 mb-1">Return (after) photos</div>
                        <PhotoStrip :photos="returnPhotosFor(a)" />
                        <button
                            v-if="a.returned_on"
                            type="button"
                            class="text-xs text-accent-primary hover:underline"
                            @click="openPhotoUpload(a, 'return')"
                        >+ add return photo</button>
                    </div>
                </div>
            </li>
        </ul>

        <!-- Issue dialog -->
        <base-modal-small v-if="issueDialogOpen" @close="issueDialogOpen = false">
            <template #header>Issue item</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div class="flex flex-col">
                        <label class="text-gray-500">Issue to*</label>
                        <select required v-model="issueForm.user" class="border rounded p-1">
                            <option :value="null">{{ usersLoading ? 'Loading users…' : 'Select…' }}</option>
                            <option v-for="u in issueUserOptions" :key="u.value" :value="u.value">{{ u.label }}</option>
                        </select>
                        <label class="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                            <input type="checkbox" v-model="includeAllUsers" />
                            <span>Include inactive users</span>
                        </label>
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Issue date</label>
                        <input type="date" v-model="issueForm.issued_on" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Condition at issue</label>
                        <input type="text" v-model="issueForm.condition_at_issue" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Notes (no credentials)</label>
                        <textarea v-model="issueForm.notes" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Before-photo (optional, camera on touch)</label>
                        <input type="file" accept="image/*" capture="environment" @change="onIssuePhotoSelected" />
                        <div v-if="issueForm.photoFile" class="text-xs text-gray-600 mt-1">
                            Selected: {{ issueForm.photoFile.name }} ({{ Math.round(issueForm.photoFile.size / 1024) }} KB)
                        </div>
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="issueDialogOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="!issueForm.user || busy" @click="submitIssue" class="px-3 py-1 rounded bg-dark-primary text-white">Issue</button>
                    </div>
                </div>
            </template>
        </base-modal-small>

        <!-- Return dialog -->
        <base-modal-small v-if="returnDialogOpen" @close="returnDialogOpen = false">
            <template #header>Record return</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div class="flex flex-col">
                        <label class="text-gray-500">Return date</label>
                        <input type="date" v-model="returnForm.returned_on" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Condition at return</label>
                        <input type="text" v-model="returnForm.condition_at_return" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Return notes (appended)</label>
                        <textarea v-model="returnForm.return_notes" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">After-photo (optional, camera on touch)</label>
                        <input type="file" accept="image/*" capture="environment" @change="onReturnPhotoSelected" />
                        <div v-if="returnForm.photoFile" class="text-xs text-gray-600 mt-1">
                            Selected: {{ returnForm.photoFile.name }} ({{ Math.round(returnForm.photoFile.size / 1024) }} KB)
                        </div>
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="returnDialogOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="busy" @click="submitReturn" class="px-3 py-1 rounded bg-dark-primary text-white">Record return</button>
                    </div>
                </div>
            </template>
        </base-modal-small>

        <!-- Retire dialog -->
        <base-modal-small v-if="retireDialogOpen" @close="retireDialogOpen = false">
            <template #header>Retire item</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div v-if="openIssueCount > 0" class="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                        <strong>{{ openIssueCount }} open issue(s)</strong> on this item. You'll need to confirm to retire it.
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Retire note (e.g. "Wiped 2026-06; Dell turn-in")</label>
                        <textarea v-model="retireForm.retire_note" class="border rounded p-1" />
                    </div>
                    <div v-if="openIssueCount > 0" class="flex items-center space-x-1">
                        <input type="checkbox" id="confirm_open_issues" v-model="retireForm.confirm_open_issues" />
                        <label for="confirm_open_issues" class="text-xs">Retire despite {{ openIssueCount }} open issue(s)</label>
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="retireDialogOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="busy" @click="submitRetire" class="px-3 py-1 rounded bg-red-600 text-white">Retire</button>
                    </div>
                </div>
            </template>
        </base-modal-small>

        <!-- Photo upload dialog (post-hoc) -->
        <base-modal-small v-if="photoUploadOpen" @close="photoUploadOpen = false">
            <template #header>Add {{ photoUploadPhase }} photo</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <input type="file" accept="image/*" capture="environment" @change="onPostHocPhotoSelected" />
                    <input type="text" placeholder="Caption (optional)" v-model="photoUploadCaption" class="border rounded p-1 w-full" />
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="photoUploadOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="!photoUploadFile || busy" @click="submitPostHocPhoto" class="px-3 py-1 rounded bg-dark-primary text-white">Upload</button>
                    </div>
                </div>
            </template>
        </base-modal-small>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { formatDate } from '@/composables/formatDate';
import { getUsers } from '@/services/User';
import PhotoStrip from './PhotoStrip.vue';
import {
    issueInventoryItem,
    recordInventoryReturn,
    retireInventoryItem,
    unretireInventoryItem,
    returnInventoryItemToStock,
    uploadAssignmentPhoto,
    patchInventoryItemUser,
} from '@/services/Inventory';

const props = defineProps(['inventoryItem', 'userOptions']);
const emit = defineEmits(['refetch']);

const store = useStore();
const $toast = useToast();
const busy = ref(false);

const assignments = computed(() => props.inventoryItem?.inventory_users || []);
const hasOpenAssignment = computed(() => assignments.value.some(a => a.is_open || a.returned_on === null));
const isRetired = computed(() => props.inventoryItem?.lifecycle_state === 'retired');
const isReturned = computed(() => props.inventoryItem?.lifecycle_state === 'returned');
const canIssue = computed(() => !hasOpenAssignment.value && !isRetired.value);
const canRetire = computed(() => !hasOpenAssignment.value && !isRetired.value);
const openIssueCount = computed(() => props.inventoryItem?.open_issue_count || 0);

const lifecycleLabel = computed(() => ({
    in_stock: 'Available',
    assigned: 'Assigned',
    returned: 'Needs review',
    retired: 'Retired',
}[props.inventoryItem?.lifecycle_state] || props.inventoryItem?.lifecycle_state));

const lifecycleBadgeClass = computed(() => ({
    in_stock: 'bg-green-100 text-green-800',
    assigned: 'bg-blue-100 text-blue-800',
    returned: 'bg-amber-100 text-amber-800',
    retired: 'bg-gray-300 text-gray-700',
}[props.inventoryItem?.lifecycle_state] || 'bg-gray-100 text-gray-700'));

const warrantyStatus = computed(() => props.inventoryItem?.warranty_status);
const warrantyStatusLabel = computed(() => ({
    active: 'active',
    expiring_soon: 'expiring soon',
    expired: 'expired',
    unknown: 'unknown',
}[warrantyStatus.value] || warrantyStatus.value));

const warrantyBadgeClass = computed(() => ({
    active: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-amber-100 text-amber-800',
    expired: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-200 text-gray-700',
}[warrantyStatus.value] || 'bg-gray-100 text-gray-700'));

function actionButtonClass(enabled) {
    if (enabled) return 'rounded px-3 py-1 text-sm bg-dark-primary text-white hover:bg-dark-secondary';
    return 'rounded px-3 py-1 text-sm bg-gray-200 text-gray-500 cursor-not-allowed';
}

function issuePhotosFor(a) {
    return (a.photos || []).filter(p => p.phase === 'issue');
}
function returnPhotosFor(a) {
    return (a.photos || []).filter(p => p.phase === 'return');
}

// --- Issue-to picker ---
// The `userOptions` prop is the grid's *filter* list (everyone not hidden from
// people pickers, plus a "clear" sentinel), which is too broad for assigning.
// Fetch the raw users here so we can key off is_active / has_groups and offer a
// toggle for the long tail. Loaded lazily — the dialog is the only consumer.
const usersRaw = ref([]);
const usersLoaded = ref(false);
const usersLoading = ref(false);
const includeAllUsers = ref(false);

async function loadUsers() {
    if (usersLoaded.value || usersLoading.value) return;
    usersLoading.value = true;
    try {
        const result = await getUsers({ endpoint: '', token: store.state.auth.token });
        usersRaw.value = result?.users || [];
        usersLoaded.value = true;
    } catch (e) {
        $toast.error(`Couldn't load users: ${describe(e)}`);
    } finally {
        usersLoading.value = false;
    }
}

const issueUserOptions = computed(() => {
    // Fall back to the prop list if the fetch hasn't landed (or failed), minus
    // the filter-only "clear" sentinel.
    if (!usersRaw.value.length) {
        return (props.userOptions || []).filter(o => o.value !== 'clear');
    }
    return usersRaw.value
        .filter(u => !u.hide_from_people_pickers)
        .filter(u => includeAllUsers.value || (u.is_active && u.has_groups))
        .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
        .map(u => {
            const flags = [];
            if (!u.is_active || !u.has_groups) flags.push('inactive');
            const suffix = flags.length ? ` (${flags.join(', ')})` : '';
            return { value: u.id, label: `${u.first_name} ${u.last_name}${suffix}` };
        });
});

// --- Issue ---
const issueDialogOpen = ref(false);
const issueForm = ref({
    user: null, issued_on: new Date().toISOString().slice(0, 10),
    condition_at_issue: '', notes: '', photoFile: null,
});

// Un-toggling can drop the selected user out of the list — don't submit an
// option the picker no longer shows.
watch(issueUserOptions, (options) => {
    if (issueForm.value.user && !options.some(o => o.value === issueForm.value.user)) {
        issueForm.value.user = null;
    }
});

function openIssueDialog() {
    issueForm.value = {
        user: null,
        issued_on: new Date().toISOString().slice(0, 10),
        condition_at_issue: props.inventoryItem?.condition || '',
        notes: '', photoFile: null,
    };
    includeAllUsers.value = false;
    issueDialogOpen.value = true;
    loadUsers();
}

function onIssuePhotoSelected(e) {
    issueForm.value.photoFile = e.target.files[0] || null;
}

async function submitIssue() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        const { photoFile, ...payload } = issueForm.value;
        const resp = await issueInventoryItem(props.inventoryItem.id, payload, token);
        const newAssignment = resp.data;
        if (photoFile) {
            try {
                await uploadAssignmentPhoto({
                    assignmentId: newAssignment.id, phase: 'issue', file: photoFile,
                }, token);
            } catch (e) {
                $toast.warning(`Item issued, but photo upload failed: ${describe(e)}`);
            }
        }
        $toast.success('Item issued.');
        issueDialogOpen.value = false;
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

// --- Return ---
const returnDialogOpen = ref(false);
const returnForm = ref({ returned_on: '', condition_at_return: '', return_notes: '', photoFile: null });

function openReturnDialog() {
    returnForm.value = {
        returned_on: new Date().toISOString().slice(0, 10),
        condition_at_return: '', return_notes: '', photoFile: null,
    };
    returnDialogOpen.value = true;
}

function onReturnPhotoSelected(e) {
    returnForm.value.photoFile = e.target.files[0] || null;
}

async function submitReturn() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        const { photoFile, ...payload } = returnForm.value;
        const resp = await recordInventoryReturn(props.inventoryItem.id, payload, token);
        const updatedAssignment = resp.data;
        if (photoFile) {
            try {
                await uploadAssignmentPhoto({
                    assignmentId: updatedAssignment.id, phase: 'return', file: photoFile,
                }, token);
            } catch (e) {
                $toast.warning(`Return recorded, but photo upload failed: ${describe(e)}`);
            }
        }
        $toast.success('Return recorded.');
        returnDialogOpen.value = false;
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

// --- Retire / Un-retire / Return-to-stock ---
const retireDialogOpen = ref(false);
const retireForm = ref({ retire_note: '', confirm_open_issues: false });

function openRetireDialog() {
    retireForm.value = { retire_note: '', confirm_open_issues: false };
    retireDialogOpen.value = true;
}

async function submitRetire() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        await retireInventoryItem(props.inventoryItem.id, retireForm.value, token);
        $toast.success('Item retired.');
        retireDialogOpen.value = false;
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

async function unretire() {
    if (!confirm('Un-retire this item back to in-stock?')) return;
    busy.value = true;
    try {
        await unretireInventoryItem(props.inventoryItem.id, store.state.auth.token);
        $toast.success('Item un-retired.');
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

async function returnToStock() {
    if (!confirm('Move this returned item back to in-stock (available for issue)?')) return;
    busy.value = true;
    try {
        await returnInventoryItemToStock(props.inventoryItem.id, store.state.auth.token);
        $toast.success('Item moved to in-stock.');
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

// --- Post-hoc photo upload ---
const photoUploadOpen = ref(false);
const photoUploadAssignment = ref(null);
const photoUploadPhase = ref('');
const photoUploadFile = ref(null);
const photoUploadCaption = ref('');

function openPhotoUpload(a, phase) {
    photoUploadAssignment.value = a;
    photoUploadPhase.value = phase;
    photoUploadFile.value = null;
    photoUploadCaption.value = '';
    photoUploadOpen.value = true;
}

function onPostHocPhotoSelected(e) {
    photoUploadFile.value = e.target.files[0] || null;
}

async function submitPostHocPhoto() {
    busy.value = true;
    try {
        await uploadAssignmentPhoto({
            assignmentId: photoUploadAssignment.value.id,
            phase: photoUploadPhase.value,
            file: photoUploadFile.value,
            caption: photoUploadCaption.value,
        }, store.state.auth.token);
        $toast.success('Photo uploaded.');
        photoUploadOpen.value = false;
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

// --- Editable assignment record (correct issued_on / conditions / notes after the fact) ---
const editingAssignmentId = ref(null);
const editForm = ref({
    issued_on: '',
    returned_on: '',
    condition_at_issue: '',
    condition_at_return: '',
    notes: '',
});

function startEditAssignment(a) {
    editingAssignmentId.value = a.id;
    editForm.value = {
        issued_on: a.issued_on || '',
        returned_on: a.returned_on || '',
        condition_at_issue: a.condition_at_issue || '',
        condition_at_return: a.condition_at_return || '',
        notes: a.notes || '',
    };
}

function cancelEditAssignment() {
    editingAssignmentId.value = null;
}

async function saveEditAssignment(a) {
    busy.value = true;
    try {
        // Trim and null-out empty strings on optional date fields so the
        // server sees nulls (not invalid empty dates).
        const payload = { ...editForm.value };
        ['issued_on', 'returned_on'].forEach(k => {
            if (payload[k] === '') payload[k] = null;
        });
        await patchInventoryItemUser(a.id, payload, store.state.auth.token);
        $toast.success('Assignment updated.');
        editingAssignmentId.value = null;
        emit('refetch');
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

function describe(e) {
    if (e?.response?.data) {
        if (typeof e.response.data === 'string') return e.response.data;
        return Object.values(e.response.data).flat().join(' · ');
    }
    return e?.message || String(e);
}
</script>
